/**
 * GA4 Tracking Utilities
 *
 * Provides helper functions for Google Analytics 4 tracking with consistent
 * agency/agent/client dimensions across agent and client applications.
 *
 * Note: GA4 API expects snake_case for property names (not camelCase).
 */

import ReactGA from 'react-ga4';

/**
 * Sets custom user properties for GA4 tracking including agency, agent, and client information
 * @param {Object} options - User properties
 * @param {Object} options.agency - Agency object with id, agencyCode, name
 * @param {Object} options.agent - Agent object with id, email, fullName
 * @param {Object} options.client - Client object with id, email, name/fullName, dealId, dealIdSource (optional)
 * @param {string} options.userType - Type of user ('agent' or 'client')
 */
export const setGA4UserProperties = ({ agency, agent, client, userType }) => {
  const userProperties = {};

  if (agency?.agencyCode) userProperties.agency_code = agency.agencyCode;
  if (agency?.name) userProperties.agency_name = agency.name;
  if (agent?.email) userProperties.agent_email = agent.email;
  if (agent?.fullName) userProperties.agent_name = agent.fullName;
  if (client?.email) userProperties.client_email = client.email;
  if (client?.name || client?.fullName) userProperties.client_name = client.name || client.fullName;
  if (client?.dealId) userProperties.deal_id = client.dealId;
  if (client?.dealIdSource) userProperties.deal_id_source = client.dealIdSource;
  if (userType) userProperties.user_type = userType;

  ReactGA.gtag('set', 'user_properties', userProperties);
};

/**
 * Sends a pageview event
 * Note: User properties (agency/agent/client) are already set via setGA4UserProperties
 * and will automatically be included with all events
 * @param {Object} options - Pageview options
 * @param {string} options.page - Page path
 * @param {string} options.title - Page title (optional)
 * @param {string} options.contentGroup - Content group (optional)
 */
export const sendGA4Pageview = ({ page, title, contentGroup }) => {
  const fieldObject = {
    hitType: 'pageview',
    page,
  };

  if (title) fieldObject.title = title;
  if (contentGroup) fieldObject.content_group = contentGroup;

  ReactGA.send(fieldObject);
};
